// Work › Memory review (`/c/{slug}/work/memory/review`; M7-04; 07 §Headless rule "Memory review
// (proposals, contradictions, decisions) is a CompanyOS screen"). Tabs: "To review" (one call,
// `memory/review`: the page candidates Recall proposed that wait for the caller, the
// contradictions and the decisions Recall reported to them), "Accepted" and "Rejected" (the
// decided promotions, `promotions?state=`). Accepting a proposal makes it a Knowledge page in a
// space the caller picks (title and kind editable); rejecting takes an optional reason.
// RECALL-CONTRADICTIONS: "To settle" lists Recall's open contradictions as pairs with Recall's
// suggestion; a person keeps a side, merges (a condition, in a modal) or dismisses. The older
// contradiction notices stay listed below as before. When the
// server has no Recall, or the company is not provisioned yet, a notice says so: what CompanyOS
// already holds still shows.
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  PageHeader,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BrainIcon,
  CheckIcon,
  FileTextIcon,
  GitCompareArrowsIcon,
  MergeIcon,
  ScaleIcon,
  SparklesIcon,
  XIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { LoadError, Loading, Section } from "../screen-parts";
import { spacesQuery } from "../work/work-queries";
import {
  type ConflictItem,
  type ContradictionItem,
  captureSettingsQuery,
  captureStatsQuery,
  type DecisionItem,
  invalidateMemory,
  type MemoryReview,
  memoryReviewQuery,
  type PromotionItem,
  promotionsQuery,
  type RecallConnection,
} from "./memory-queries";

export const MEMORY_TABS = ["review", "accepted", "rejected"] as const;
export type MemoryTab = (typeof MEMORY_TABS)[number];
const TAB_LABELS: Record<MemoryTab, string> = {
  review: "To review",
  accepted: "Accepted",
  rejected: "Rejected",
};

const PROPOSAL_KIND: Record<string, string> = {
  promote: "Knowledge",
  procedure: "Procedure",
  principle: "Principle",
  rule: "Rule",
  "record:Procedure": "Procedure",
  "record:Constraint": "Constraint",
  "record:Principle": "Principle",
};
const kindLabel = (k: string) => PROPOSAL_KIND[k] ?? k;

const SCOPE_LABEL = {
  company: "Company",
  space: "Space",
  team: "Team",
  seat: "Seat",
  agency: "Agency",
  agent: "Helper",
} as const;

/** The page kind a proposal becomes by default (as the API decides it). */
function defaultKind(proposalKind: string): "page" | "sop" | "policy" {
  const k = proposalKind.toLowerCase();
  if (k === "procedure" || k === "record:procedure") return "sop";
  if (k === "rule" || k === "record:constraint") return "policy";
  return "page";
}

/**
 * Why there may be nothing from Recall: the connection, said once at the top. Phase 1 inc 3:
 * `memory/search` also answers `not_set_up` (Recall is connected, but this person cannot be read
 * for yet): never shown as "nothing found".
 */
export function RecallNotice({
  recall,
}: {
  recall: { state: RecallConnection["state"] | "not_set_up"; detail: string | null };
}) {
  if (recall.state === "connected") return null;
  const title =
    recall.state === "not_configured"
      ? "Recall is not connected"
      : recall.state === "not_provisioned"
        ? "Recall is being set up for this company"
        : recall.state === "not_set_up"
          ? "Memory isn't set up for you yet"
          : "Recall did not answer";
  return (
    <Notice
      tone={recall.state === "unavailable" ? "warning" : "info"}
      title={title}
      data-recall-state={recall.state}
    >
      {recall.detail}
    </Notice>
  );
}

export function MemoryReviewScreen({
  api,
  company,
  tab,
  onTab,
  scope = null,
  onClearScope,
}: {
  api: Api;
  company: CompanyAccess;
  tab: MemoryTab;
  onTab: (tab: MemoryTab) => void;
  /** INBOX-READABLE: only this memory scope's proposals and contradictions (`?scope=`). */
  scope?: string | null;
  onClearScope?: () => void;
}) {
  return (
    <>
      <PageHeader
        spacing="tabs"
        eyebrow="Work"
        title="Memory review"
        subtitle="What Recall learned that needs a person: pages to promote, contradictions to settle and decisions it recorded."
      />
      <ViewTabs
        aria-label="Memory review"
        value={tab}
        onValueChange={(v) => onTab(v as MemoryTab)}
        tabs={MEMORY_TABS.map((t) => ({ value: t, label: TAB_LABELS[t] }))}
      />
      <div className="flex min-w-0 flex-col gap-3 pt-5">
        {tab === "review" ? (
          <ReviewTab api={api} company={company} scope={scope} onClearScope={onClearScope} />
        ) : (
          <DecidedTab api={api} company={company} state={tab} />
        )}
      </div>
    </>
  );
}

function ReviewTab({
  api,
  company,
  scope,
  onClearScope,
}: {
  api: Api;
  company: CompanyAccess;
  scope: string | null;
  onClearScope: (() => void) | undefined;
}) {
  const q = useQuery(memoryReviewQuery(api, company.id));
  if (q.isPending) return <Loading label="Loading memory review" rows={4} />;
  if (q.isError) return <LoadError what="Memory review" error={q.error} />;
  // INBOX-READABLE: one scope's proposals and contradictions (decisions carry no scope).
  const r = scope
    ? {
        ...q.data,
        proposals: q.data.proposals.filter((p) => p.scope_id === scope),
        contradictions: q.data.contradictions.filter((c) => c.scope.id === scope),
        conflicts: q.data.conflicts.filter((c) => c.scope.id === scope),
        conflicts_unsent: q.data.conflicts_unsent.filter((c) => c.scope.id === scope),
      }
    : q.data;
  const scopeName =
    q.data.proposals.find((p) => p.scope_id === scope)?.scope_name ??
    q.data.contradictions.find((c) => c.scope.id === scope)?.scope.name ??
    "One scope";
  return (
    <>
      <RecallNotice recall={r.recall} />
      {q.data.proposal_digest && q.data.proposal_digest.pending > 0 ? (
        <ProposalDigest api={api} company={company} digest={q.data.proposal_digest} />
      ) : null}
      {scope ? (
        <div data-slot="memory-scope-filter" className="flex items-center gap-1.5">
          <span className="inline-flex h-6 items-center gap-0.5 rounded-full border border-border bg-secondary pr-0.5 pl-2.5 text-xs font-medium text-foreground">
            {scopeName}
            {onClearScope ? (
              <button
                type="button"
                aria-label={`Remove filter: ${scopeName}`}
                onClick={onClearScope}
                className="flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-sunken hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <XIcon aria-hidden="true" className="size-3" />
              </button>
            ) : null}
          </span>
          <InfoTip label="About this filter">
            Only this scope's proposals and contradictions. Decisions show in full.
          </InfoTip>
        </div>
      ) : null}
      <Section
        id="memory-proposals"
        title={`Pages to promote (${r.proposals.length})`}
        description="Recall noticed knowledge worth keeping. Accept it as a page in a space, or reject it."
      >
        {r.proposals.length ? (
          <div className="flex flex-col gap-3" data-slot="memory-proposals">
            {r.proposals.map((p) => (
              <ProposalCard key={p.id} api={api} company={company} promotion={p} />
            ))}
          </div>
        ) : (
          <EmptyState
            variant="card"
            icon={FileTextIcon}
            title="Nothing to promote"
            description="When Recall proposes a procedure, rule or principle for a scope you review, it waits here."
          />
        )}
      </Section>
      <Section
        id="memory-conflicts"
        title={`To settle (${scope ? r.conflicts.length : q.data.conflicts_open})`}
        description="Two memories that disagree. Keep one, merge them, or dismiss."
      >
        {q.data.conflicts_unchecked > 0 ? (
          <p
            className="mb-3 text-sm text-warning"
            role="status"
            data-slot="memory-conflicts-unchecked"
          >
            Couldn't check {q.data.conflicts_unchecked}{" "}
            {q.data.conflicts_unchecked === 1 ? "space" : "spaces"}. Their contradictions may be
            missing here.
          </p>
        ) : null}
        {q.data.conflicts_possibly_more > 0 ? (
          <p
            className="mb-3 text-sm text-muted-foreground"
            role="status"
            data-slot="memory-conflicts-possibly-more"
          >
            Possibly more in {q.data.conflicts_possibly_more}{" "}
            {q.data.conflicts_possibly_more === 1 ? "space" : "spaces"}: memory lists 200 at a time.
          </p>
        ) : null}
        {r.conflicts.length ? (
          <div className="flex flex-col gap-3" data-slot="memory-conflicts">
            {r.conflicts.map((c) => (
              <ConflictCard key={c.id} api={api} company={company} conflict={c} />
            ))}
          </div>
        ) : q.data.conflicts_unchecked > 0 ? null : (
          <EmptyState
            variant="card"
            icon={GitCompareArrowsIcon}
            title="Nothing to settle"
            description="When two memories in your scopes disagree, both sides wait here."
          />
        )}
        {r.conflicts_unsent.length ? (
          <div className="mt-4 flex flex-col gap-2" data-slot="memory-conflicts-unsent">
            <h3 className="text-sm font-medium">Settled here, not yet in Recall</h3>
            {r.conflicts_unsent.map((c) => (
              <div
                key={c.id}
                className="rounded-md border border-border p-3 text-sm"
                data-unsent={c.id}
              >
                <p className="break-words">
                  {c.a.title} vs {c.b.title}:{" "}
                  {c.action === "keep_a"
                    ? "kept A"
                    : c.action === "keep_b"
                      ? "kept B"
                      : c.action === "merge"
                        ? "merged"
                        : "dismissed"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {c.recall_note ?? "Waiting to be sent to Recall"}
                  {c.recall_retry_at ? ` · retry ${formatDateTime(c.recall_retry_at)}` : ""}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </Section>
      <Section
        id="memory-contradictions"
        title={`Contradictions (${r.contradictions.length})`}
        description="Two records that cannot both hold. A person settles them; nothing is changed automatically."
      >
        {r.contradictions.length ? (
          <div className="flex flex-col gap-3" data-slot="memory-contradictions">
            {r.contradictions.map((c) => (
              <ContradictionCard key={c.id} contradiction={c} />
            ))}
          </div>
        ) : (
          <EmptyState
            variant="card"
            icon={GitCompareArrowsIcon}
            title="No contradictions"
            description="Recall tells the reviewer of a scope when two of its records disagree."
          />
        )}
      </Section>
      <Section
        id="memory-decisions"
        title={`Decisions (${r.decisions.length})`}
        description="Outcomes Recall recorded for decisions in your scopes, newest first."
      >
        {r.decisions.length ? (
          <div
            className="flex flex-col rounded-lg border border-border"
            data-slot="memory-decisions"
          >
            {r.decisions.map((d) => (
              <DecisionRow key={d.id} decision={d} />
            ))}
          </div>
        ) : (
          <EmptyState
            variant="card"
            icon={ScaleIcon}
            title="No decisions yet"
            description="A decision's outcome lands here when Recall records it."
          />
        )}
      </Section>
      {isManager(company) && !scope ? <CaptureSection api={api} company={company} /> : null}
    </>
  );
}

const CAPTURE_SOURCES = [
  {
    key: "chat",
    label: "Chat",
    tip: "Topics in shared channels. Never DMs or private channels.",
  },
  { key: "task_threads", label: "Task threads", tip: "Comments on tasks a person took part in." },
  { key: "runs", label: "Runs", tip: "Agents' run threads and run outcomes. Off by default." },
] as const;

const SOURCE_LABEL: Record<string, string> = {
  chat: "Chat",
  task_threads: "Task threads",
  runs: "Runs",
  task: "Tasks",
  approval: "Approvals",
  decision: "Decisions",
  meeting: "Meetings",
  priority: "Priorities",
  crm_record: "CRM records",
  crm_activity: "CRM activity",
  other: "Other",
};

/**
 * CAPTURE-LESS-NOISE (managers): which conversations go to memory, by source, and what was sent
 * per day with the records Recall made of it. A conversation goes once, whole, after it is quiet,
 * and only when a person took part.
 */
function CaptureSection({ api, company }: { api: Api; company: CompanyAccess }) {
  const qc = useQueryClient();
  const settings = useQuery(captureSettingsQuery(api, company.id));
  const stats = useQuery(captureStatsQuery(api, company.id));
  const update = useMutation({
    mutationFn: (body: Record<string, boolean | null>) =>
      api.client.memory.captureUpdate({ params: { co: company.id }, body }),
    onSuccess: (data) => {
      qc.setQueryData(captureSettingsQuery(api, company.id).queryKey, data);
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Nothing was changed. Try again."), type: "error" }),
  });
  const s = settings.data;
  const rows = stats.data?.rows ?? [];
  return (
    <Section
      id="memory-capture"
      title="Capture"
      actions={
        <InfoTip label="About capture">
          A conversation goes to memory once, whole, after {s?.quiet_minutes ?? 30} quiet minutes or
          when its task closes, and only when a person took part. Agent-only threads, quick replies,
          digests and notices never go. Agents save on purpose, with a reason.
        </InfoTip>
      }
    >
      {settings.isError ? <LoadError what="Capture settings" error={settings.error} /> : null}
      <div
        data-slot="memory-capture-sources"
        className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border px-4 py-3"
      >
        {CAPTURE_SOURCES.map((src) => (
          <div key={src.key} className="flex items-center gap-2">
            <Switch
              id={`capture-${src.key}`}
              checked={s?.effective[src.key] ?? false}
              disabled={!s || update.isPending}
              onCheckedChange={(v) =>
                update.mutate({ [src.key]: v === s?.platform[src.key] ? null : v })
              }
            />
            <label htmlFor={`capture-${src.key}`} className="text-sm">
              {src.label}
            </label>
            <InfoTip label={`About ${src.label}`}>{src.tip}</InfoTip>
          </div>
        ))}
      </div>
      {rows.length ? (
        <div className="rounded-lg border border-border" data-slot="memory-capture-stats">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Captures</TableHead>
                <TableHead className="text-right">Records</TableHead>
                <TableHead className="text-right">Skipped</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.day}-${r.source}`}>
                  <TableCell>{r.day}</TableCell>
                  <TableCell>{SOURCE_LABEL[r.source] ?? r.source}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.captures}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.records}
                    {r.pending ? (
                      <span className="text-muted-foreground"> (+{r.pending} pending)</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.skipped}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : stats.isError ? (
        <LoadError what="Captures" error={stats.error} />
      ) : stats.isPending ? null : (
        <EmptyState
          variant="card"
          icon={BrainIcon}
          title="Nothing captured yet"
          description="Each day's captures and the records Recall made show here."
        />
      )}
    </Section>
  );
}

/**
 * MEMORY-HYGIENE: the memory proposals waiting for the caller, as counts, with the two bulk
 * actions (approve every exact merge; dismiss every one from a retired agent). The inbox carries
 * one digest a day; unreviewed ones expire on their own.
 */
function ProposalDigest({
  api,
  company,
  digest: d,
}: {
  api: Api;
  company: CompanyAccess;
  digest: NonNullable<MemoryReview["proposal_digest"]>;
}) {
  const qc = useQueryClient();
  const bulk = useMutation({
    mutationFn: (action: "approve_exact_merges" | "dismiss_retired_agents") =>
      api.client.memory.proposalsBulk({ params: { co: company.id }, body: { action } }),
    onSuccess: (r) => {
      toast.add({
        title:
          r.action === "approve_exact_merges"
            ? `${r.settled} exact merge(s) approved`
            : `${r.settled} proposal(s) from retired agents dismissed`,
        type: "success",
      });
      invalidateMemory(qc, company.id);
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Nothing was settled. Try again."), type: "error" }),
  });
  return (
    <Card className="min-w-0 p-4" data-slot="memory-digest">
      <div className="flex min-w-0 flex-row flex-wrap items-center gap-3">
        <BrainIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 items-center gap-1 text-sm whitespace-nowrap">
          <strong className="font-semibold tabular-nums">{d.pending}</strong> memory proposals
          waiting
          <InfoTip label="About memory proposals">
            Merges, topics and links Recall suggests for your scopes. You get one inbox digest a
            day; any left unreviewed expire after {d.expire_days} days.
          </InfoTip>
        </span>
        {d.can_decide ? (
          <span className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="outline"
              disabled={bulk.isPending || d.exact_merges === 0}
              onClick={() => bulk.mutate("approve_exact_merges")}
            >
              <CheckIcon aria-hidden="true" />
              Approve exact merges ({d.exact_merges})
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={bulk.isPending || d.from_retired_agents === 0}
              onClick={() => bulk.mutate("dismiss_retired_agents")}
            >
              <XIcon aria-hidden="true" />
              Dismiss from retired agents ({d.from_retired_agents})
            </Button>
          </span>
        ) : null}
      </div>
      {d.by_kind?.length || d.by_scope?.length ? (
        <div className="mt-3 flex min-w-0 flex-wrap gap-1.5" data-slot="memory-digest-groups">
          {d.by_kind?.map((k) => (
            <Badge key={`k:${k.kind}`} variant="accent">
              {kindLabel(k.kind)} <span className="tabular-nums">{k.count}</span>
            </Badge>
          ))}
          {d.by_scope?.map((s) => (
            <Badge key={`s:${s.scope_kind}:${s.scope_id}`} variant="outline">
              {s.name ?? SCOPE_LABEL[s.scope_kind]} <span className="tabular-nums">{s.count}</span>
            </Badge>
          ))}
        </div>
      ) : null}
      {d.top?.length ? (
        <ul className="mt-3 flex min-w-0 flex-col gap-1 text-sm" data-slot="memory-digest-top">
          {d.top.map((t) => (
            <li key={t.id} className="flex min-w-0 items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{t.title}</span>
              <Badge variant="accent">{kindLabel(t.kind)}</Badge>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                expires {formatDateTime(t.expires_at)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function ScopeBadge({ kind, name }: { kind: keyof typeof SCOPE_LABEL; name: string | null }) {
  return (
    <Badge variant="outline" title={SCOPE_LABEL[kind]}>
      {name ?? SCOPE_LABEL[kind]}
    </Badge>
  );
}

function ProposalCard({
  api,
  company,
  promotion: p,
}: {
  api: Api;
  company: CompanyAccess;
  promotion: PromotionItem;
}) {
  const headingId = useId();
  return (
    <Card
      className="flex min-w-0 flex-col gap-3 p-4"
      data-promotion={p.id}
      aria-labelledby={headingId}
      role="article"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <h3 id={headingId} className="min-w-0 flex-1 basis-60 font-heading text-sm font-semibold">
          {p.suggested_title}
        </h3>
        <Badge variant="accent">{kindLabel(p.proposal_kind)}</Badge>
        <ScopeBadge kind={p.scope_kind} name={p.scope_name} />
      </div>
      <p className="line-clamp-4 text-sm whitespace-pre-line text-muted-foreground">{p.body}</p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground tabular-nums">
          Proposed {formatDateTime(p.created_at)}
        </span>
        {p.can_decide ? (
          <span className="flex flex-wrap gap-1.5">
            <AcceptDialog api={api} company={company} promotion={p} />
            <RejectPromotionDialog api={api} company={company} promotion={p} />
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            The holder of the scope's reviewer seat decides this.
          </span>
        )}
      </div>
    </Card>
  );
}

export function AcceptDialog({
  api,
  company,
  promotion: p,
}: {
  api: Api;
  company: CompanyAccess;
  promotion: PromotionItem;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const spaces = useQuery({ ...spacesQuery(api, company.id), enabled: open });
  const live = (spaces.data?.data ?? []).filter((s) => !s.archived_at && s.kind !== "personal");
  const preferred = p.scope_kind === "space" ? p.scope_id : null;
  const [spaceId, setSpaceId] = useState<string>("");
  const [title, setTitle] = useState(p.suggested_title);
  const [kind, setKind] = useState(defaultKind(p.proposal_kind));
  const ids = { space: useId(), title: useId(), kind: useId() };
  const chosen =
    spaceId ||
    (preferred && live.some((s) => s.id === preferred) ? preferred : (live[0]?.id ?? ""));
  const accept = useMutation({
    mutationFn: () =>
      api.client.promotions.accept({
        params: { co: company.id, id: p.id },
        body: { space_id: chosen, title: title.trim(), kind },
      }),
    onSuccess: (r) => {
      setOpen(false);
      toast.add({
        title: "Promoted to a page",
        type: "success",
        ...(r.page_id
          ? {
              description: (
                <Link
                  to="/c/$slug/work/p/$pageId"
                  params={{ slug: company.slug, pageId: r.page_id }}
                  search={keepFocus}
                  className="underline"
                >
                  Open “{title.trim()}”
                </Link>
              ),
            }
          : {}),
      });
    },
    onSettled: () => invalidateMemory(qc, company.id),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" onClick={() => setOpen(true)} aria-label={`Accept: ${p.suggested_title}`}>
        <CheckIcon aria-hidden="true" />
        Accept
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Promote to a page</DialogTitle>
          <DialogDescription>
            The proposal becomes a Knowledge page with Recall's text as its body. You can edit it
            after.
          </DialogDescription>
        </DialogHeader>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor={ids.space}>Space</FieldLabel>
            <NativeSelect
              id={ids.space}
              value={chosen}
              disabled={spaces.isPending}
              onChange={(e) => setSpaceId(e.target.value)}
            >
              {live.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <FieldDescription>You need to be able to write in the space.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.title}>Title</FieldLabel>
            <Input id={ids.title} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.kind}>Kind</FieldLabel>
            <NativeSelect
              id={ids.kind}
              value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
            >
              <NativeSelectOption value="page">Page</NativeSelectOption>
              <NativeSelectOption value="sop">SOP</NativeSelectOption>
              <NativeSelectOption value="policy">Policy</NativeSelectOption>
            </NativeSelect>
          </Field>
        </FieldGroup>
        {accept.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(accept.error, "The proposal could not be promoted.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            disabled={!chosen || !title.trim() || accept.isPending}
            onClick={() => accept.mutate()}
          >
            {accept.isPending ? "Promoting…" : "Make the page"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectPromotionDialog({
  api,
  company,
  promotion: p,
}: {
  api: Api;
  company: CompanyAccess;
  promotion: PromotionItem;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const id = useId();
  const reject = useMutation({
    mutationFn: () =>
      api.client.promotions.reject({
        params: { co: company.id, id: p.id },
        body: reason.trim() ? { reason: reason.trim() } : {},
      }),
    onSuccess: () => {
      setOpen(false);
      toast.add({ title: "Proposal rejected", type: "success" });
    },
    onSettled: () => invalidateMemory(qc, company.id),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={`Reject: ${p.suggested_title}`}
      >
        <XIcon aria-hidden="true" />
        Reject
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject “{p.suggested_title}”?</DialogTitle>
          <DialogDescription>
            No page is made. Recall's own record is not changed.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>Reason (optional)</FieldLabel>
          <Textarea
            id={id}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Already covered by the refunds policy."
          />
        </Field>
        {reject.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(reject.error, "The proposal could not be rejected.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button variant="destructive" disabled={reject.isPending} onClick={() => reject.mutate()}>
            {reject.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const WINNER_LABEL = { a: "Keep A", b: "Keep B", both: "Merge", neither: "Dismiss" } as const;

/**
 * RECALL-CONTRADICTIONS: one pair Recall lists, both sides side by side with Recall's suggestion.
 * A person keeps a side, merges (both hold, each under a condition they write) or dismisses;
 * nothing is settled for them.
 */
export function ConflictCard({
  api,
  company,
  conflict: c,
}: {
  api: Api;
  company: CompanyAccess;
  conflict: ConflictItem;
}) {
  const qc = useQueryClient();
  const settle = useMutation({
    mutationFn: (body: { action: "keep_a" | "keep_b" | "merge" | "dismiss"; condition?: string }) =>
      api.client.memory.conflictSettle({ params: { co: company.id, id: c.id }, body }),
    onSuccess: (r, body) => {
      toast.add({
        title: r.routed_to_owner
          ? "Sent to the space owner to decide"
          : body.action === "dismiss"
            ? "Dismissed"
            : body.action === "merge"
              ? "Merged"
              : `Kept ${body.action === "keep_a" ? "A" : "B"}`,
        type: "success",
      });
      invalidateMemory(qc, company.id);
    },
    onError: (e) => toast.add({ title: problemText(e, "Not settled. Try again."), type: "error" }),
  });
  const s = c.suggestion;
  const sides = [
    { key: "a" as const, side: c.a },
    { key: "b" as const, side: c.b },
  ];
  return (
    <Card
      className="group flex min-w-0 flex-col gap-3 p-4"
      data-conflict={c.id}
      role="article"
      aria-label={`${c.a.title} vs ${c.b.title}`}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <ScopeBadge kind={c.scope.kind} name={c.scope.name} />
        {c.pairs > 1 ? <Badge variant="outline">{c.pairs} pairs</Badge> : null}
        {c.since ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            Since {formatDateTime(c.since)}
          </span>
        ) : null}
        {c.why ? <InfoTip label="Why this is a contradiction">{c.why}</InfoTip> : null}
        {c.routed_to_owner ? (
          <Badge variant="outline" data-slot="conflict-routed">
            Waiting for the space owner{c.routed_by ? ` · asked by ${c.routed_by}` : ""}
          </Badge>
        ) : null}
      </div>
      <div className="grid min-w-0 gap-2 sm:grid-cols-2">
        {sides.map(({ key, side }) => (
          <div
            key={key}
            data-side={key}
            className={`min-w-0 rounded-md border p-3 ${
              s?.winner === key ? "border-primary/40 bg-primary-soft/40" : "border-border bg-sunken"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-2xs font-semibold text-muted-foreground uppercase">
                {key}
              </span>
              <span className="text-2xs text-muted-foreground uppercase">{side.type}</span>
              {s?.winner === key ? (
                <Badge variant="accent" className="ml-auto">
                  Suggested
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 text-sm font-medium break-words">{side.title}</p>
            {side.abstract ? (
              <p className="mt-1 line-clamp-3 text-xs text-muted-foreground break-words">
                {side.abstract}
              </p>
            ) : null}
            {side.author || side.updated ? (
              <p className="mt-1.5 text-2xs text-muted-foreground tabular-nums">
                {[side.author, side.updated ? formatDateTime(side.updated) : null]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : null}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
          data-slot="conflict-suggestion"
        >
          {s ? (
            <>
              <SparklesIcon aria-hidden="true" className="size-3.5 shrink-0" />
              Suggested: {WINNER_LABEL[s.winner]}
              <InfoTip label="About this suggestion">
                {s.reason ?? "A suggestion only: you decide."}{" "}
                {s.source === "agent" ? "Suggested by an agent." : "Recall's own reading."}
              </InfoTip>
            </>
          ) : (
            "No suggestion"
          )}
        </span>
        {c.can_decide ? (
          <span className="flex flex-wrap gap-1.5" data-slot="conflict-actions">
            <Button
              size="sm"
              variant={s?.winner === "a" ? "default" : "outline"}
              disabled={settle.isPending}
              onClick={() => settle.mutate({ action: "keep_a" })}
              aria-label={`Keep A: ${c.a.title}`}
              data-needs-owner={c.keep_a_needs_owner || undefined}
            >
              <CheckIcon aria-hidden="true" />
              {c.keep_a_needs_owner ? "Keep A: needs space owner" : "Keep A"}
            </Button>
            <Button
              size="sm"
              variant={s?.winner === "b" ? "default" : "outline"}
              disabled={settle.isPending}
              onClick={() => settle.mutate({ action: "keep_b" })}
              aria-label={`Keep B: ${c.b.title}`}
              data-needs-owner={c.keep_b_needs_owner || undefined}
            >
              <CheckIcon aria-hidden="true" />
              {c.keep_b_needs_owner ? "Keep B: needs space owner" : "Keep B"}
            </Button>
            <MergeDialog
              conflict={c}
              pending={settle.isPending}
              onMerge={(condition) => settle.mutate({ action: "merge", condition })}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={settle.isPending}
              onClick={() => settle.mutate({ action: "dismiss" })}
              aria-label="Dismiss: not a real conflict"
            >
              <XIcon aria-hidden="true" />
              Dismiss
            </Button>
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            A person who reviews this scope decides.
          </span>
        )}
      </div>
    </Card>
  );
}

/** Merge: both hold; the person says when each one does. */
function MergeDialog({
  conflict: c,
  pending,
  onMerge,
}: {
  conflict: ConflictItem;
  pending: boolean;
  onMerge: (condition: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [condition, setCondition] = useState("");
  const id = useId();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        disabled={pending}
        onClick={() => setOpen(true)}
        aria-label="Merge: both hold"
      >
        <MergeIcon aria-hidden="true" />
        Merge
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge both</DialogTitle>
          <DialogDescription>
            Both stay true, each under its own condition. Say when each one holds.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>When each holds</FieldLabel>
          <Textarea
            id={id}
            value={condition}
            maxLength={500}
            onChange={(e) => setCondition(e.target.value)}
            placeholder={`“${c.a.title}” in general; “${c.b.title}” when …`}
          />
        </Field>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            disabled={!condition.trim() || pending}
            onClick={() => {
              onMerge(condition.trim());
              setOpen(false);
            }}
          >
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContradictionCard({ contradiction: c }: { contradiction: ContradictionItem }) {
  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4" data-contradiction={c.id}>
      <div className="flex flex-wrap items-center gap-2">
        <ScopeBadge kind={c.scope.kind} name={c.scope.name} />
        <span className="text-xs text-muted-foreground tabular-nums">
          Reported {formatDateTime(c.received_at)}
        </span>
      </div>
      <div className="grid min-w-0 gap-2 sm:grid-cols-2">
        {[c.a, c.b].map((side, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: the two sides are fixed
            key={i}
            className="min-w-0 rounded-md border border-border bg-sunken p-3"
          >
            <p className="text-2xs text-muted-foreground uppercase">{side.type}</p>
            <p className="text-sm break-words">{side.title}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DecisionRow({ decision: d }: { decision: DecisionItem }) {
  return (
    <div
      className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-3 last:border-b-0"
      data-decision={d.id}
    >
      <span className="min-w-0 flex-1 basis-60 text-sm font-medium break-words">{d.title}</span>
      <Badge variant={d.decision === "(outcome cleared)" ? "neutral" : "success"}>
        {d.decision}
      </Badge>
      <span className="text-xs text-muted-foreground tabular-nums">
        {formatDateTime(d.created_at)}
      </span>
    </div>
  );
}

function DecidedTab({
  api,
  company,
  state,
}: {
  api: Api;
  company: CompanyAccess;
  state: "accepted" | "rejected";
}) {
  const q = useQuery(promotionsQuery(api, company.id, state));
  if (q.isPending) return <Loading label={`Loading ${state} proposals`} rows={3} />;
  if (q.isError) return <LoadError what="Proposals" error={q.error} />;
  const rows = q.data.data;
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={BrainIcon}
        title={state === "accepted" ? "Nothing promoted yet" : "Nothing rejected"}
        description={
          state === "accepted"
            ? "Accepted proposals become pages; they are listed here with a link to the page."
            : "Rejected proposals are kept here so the decision can be looked up."
        }
      />
    );
  return (
    <div className="flex flex-col rounded-lg border border-border" data-slot={`memory-${state}`}>
      {rows.map((p) => (
        <div
          key={p.id}
          className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-3 last:border-b-0"
          data-promotion={p.id}
        >
          <span className="min-w-0 flex-1 basis-60 text-sm font-medium break-words">
            {p.page_id ? (
              <Link
                to="/c/$slug/work/p/$pageId"
                params={{ slug: company.slug, pageId: p.page_id }}
                search={keepFocus}
                className="underline-offset-4 hover:underline"
              >
                {p.suggested_title}
              </Link>
            ) : (
              p.suggested_title
            )}
          </span>
          <Badge variant="outline">{kindLabel(p.proposal_kind)}</Badge>
          <span className="text-xs text-muted-foreground tabular-nums">
            {p.decided_at ? formatDateTime(p.decided_at) : ""}
          </span>
        </div>
      ))}
    </div>
  );
}
